import React from 'react';
import {
	Breadcrumb,
	Card,
	Input,
	Button,
	Table,
	Modal,
	Form,
	message,
	Alert,
	Tag,
	Tooltip,
	Typography,
} from 'antd';
import {
	SearchOutlined,
	FormOutlined,
	RedoOutlined,
	BankOutlined,
	SaveOutlined,
	CloseCircleOutlined,
	CloseOutlined,
	IdcardOutlined,
	UserOutlined,
} from '@ant-design/icons';

import { connect } from 'react-redux';
import {
	initHandler,
	ajaxViewHandler,
	ajaxHandler,
	setConfig,
} from "../../store/actions";

import Config from '../../Config';
import { Helmet } from "react-helmet";
import ErrorAlertModal from '../../components/ErrorAlertModal';

const { Text } = Typography;

// Halaman khusus superadmin: kelola data rekening tujuan setoran per kabupaten.
// Hanya field inisial, nomor_rekening, dan nama_penerima yang boleh diubah dari sini -
// data lain (nama kabupaten, provinsi) bersifat referensi dan tidak diedit di halaman ini.
const configClass = {
	title: 'Kelola Kabupaten',
	apiUrl: { data: 'master/api/kabupaten/' },
	breadCrumb: [
		<span><BankOutlined /> Master</span>,
		'Kabupaten',
	],
};

const config = new Config();

const FIELD_STYLE = {
	borderRadius: 8,
	height: 40,
};

class DataKabupaten extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			errMsg: '',
			errTitle: '',
			loadingData: false,
			loadingButton: false,
			searchText: '',
			dataSource: [],
			isMobile: window.innerWidth <= 768,

			modalVisible: false,
			editingRow: null,
		};
		this._searchTimer = null;
	}

	UNSAFE_componentWillMount() {
		this.props.initHandler();
	}

	componentDidMount() {
		this.getData();
		this._handleResize = () => this.setState({ isMobile: window.innerWidth <= 768 });
		window.addEventListener('resize', this._handleResize);
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this._handleResize);
		clearTimeout(this._searchTimer);
	}

	async getData(search = this.state.searchText) {
		this.setState({ loadingData: true });
		// Tanpa parameter `fiskal` -> backend (MasterRegenciesViewset.get_queryset)
		// otomatis hanya mengembalikan kabupaten/kota provinsi 62.
		this.props.ajaxViewHandler('get', configClass.apiUrl.data + '?limit=100&offset=0&search=' + search)
			.then(() => {
				const data = this.props.responseMsg;
				if (this.props.isSuccess) {
					this.setState({ dataSource: data.results || data });
				} else {
					this.setState({ errTitle: 'Gagal memuat data', errMsg: this.props.responseMsg });
					message.error({ content: 'Gagal memuat data', duration: 2, key: 'toast' });
				}
				this.setState({ loadingData: false });
			})
			.catch(() => {
				message.error({ content: 'Gagal request data, coba lagi', duration: 2, key: 'toast' });
				this.setState({ loadingData: false });
			});
	}

	handleSearchChange = (e) => {
		const val = e.target.value;
		this.setState({ searchText: val });
		clearTimeout(this._searchTimer);
		this._searchTimer = setTimeout(() => {
			this.getData(val);
		}, 400);
	};

	openEdit = (row) => {
		// `editingRow` di-set bersamaan dengan modalVisible dalam satu setState,
		// jadi saat Form pertama kali mount (lihat destroyOnClose di Modal) nilai
		// `initialValues`-nya sudah benar - tidak perlu setFieldsValue via ref yang
		// rawan race condition dengan animasi mount Modal.
		this.setState({ modalVisible: true, editingRow: row });
	};

	closeEdit = () => {
		this.setState({ modalVisible: false, editingRow: null });
	};

	handleSubmit = (values) => {
		const { editingRow } = this.state;
		this.setState({ loadingButton: true });
		message.loading({ content: 'Menyimpan...', key: 'toast' });
		// PATCH hanya mengirim 3 field yang diizinkan - field lain (name, province) tidak tersentuh.
		this.props.ajaxHandler('patch', configClass.apiUrl.data + editingRow.id + '/', values, false, true)
			.then(() => {
				this.setState({ loadingButton: false });
				if (this.props.isSuccess) {
					message.success({ content: 'Data rekening kabupaten berhasil disimpan', duration: 2, key: 'toast' });
					this.closeEdit();
					this.getData();
				} else {
					const errTitle = 'Gagal menyimpan data';
					message.error({ content: errTitle, duration: 2, key: 'toast' });
					this.setState({ errTitle, errMsg: this.props.responseMsg });
				}
			})
			.catch(() => {
				this.setState({ loadingButton: false });
				message.error({ content: 'Kesalahan pengiriman data, coba lagi', duration: 2, key: 'toast' });
			});
	};

	_buildColumns() {
		return [
			{
				title: 'Kabupaten/Kota',
				dataIndex: 'name',
				key: 'name',
				render: (value) => (
					<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
						<div style={{
							width: 34, height: 34, borderRadius: 8,
							background: 'linear-gradient(135deg, #0f2847, #1e4d8a)',
							display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
						}}>
							<BankOutlined style={{ color: '#fff', fontSize: 15 }} />
						</div>
						<div style={{ fontWeight: 600, fontSize: 13, color: '#222' }}>{value || '—'}</div>
					</div>
				),
			},
			{
				title: 'Inisial',
				dataIndex: 'inisial',
				key: 'inisial',
				width: 110,
				align: 'center',
				render: (val) => val
					? <Tag style={{ borderRadius: 20, fontWeight: 600, fontSize: 12, background: '#e6f0fb', borderColor: '#91b4e8', color: '#1e4d8a' }}>{val}</Tag>
					: <Text type="secondary">—</Text>,
			},
			{
				title: 'Nomor Rekening',
				dataIndex: 'nomor_rekening',
				key: 'nomor_rekening',
				render: (val) => val
					? <span style={{ fontFamily: 'monospace', fontSize: 13 }}>{val}</span>
					: <Text type="secondary">—</Text>,
			},
			{
				title: 'Nama Penerima',
				dataIndex: 'nama_penerima',
				key: 'nama_penerima',
				render: (val) => val || <Text type="secondary">—</Text>,
			},
			{
				title: 'Aksi',
				key: 'aksi',
				width: 90,
				fixed: 'right',
				align: 'center',
				render: (_, row) => (
					<Tooltip title="Edit rekening">
						<Button size="small" shape="circle" type="primary" ghost onClick={() => this.openEdit(row)}>
							<FormOutlined />
						</Button>
					</Tooltip>
				),
			},
		];
	}

	renderMobileCard(row) {
		return (
			<Card
				key={row.id}
				style={{
					borderRadius: 14,
					marginBottom: 12,
					boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
					border: '1px solid #f0f0f0',
					overflow: 'hidden',
				}}
				bodyStyle={{ padding: 0 }}
			>
				<div style={{
					background: 'linear-gradient(135deg, #0f284711, #1e4d8a11)',
					borderBottom: '1px solid #d0dff5',
					padding: '12px 16px',
					display: 'flex',
					alignItems: 'center',
					gap: 12,
				}}>
					<div style={{
						width: 40,
						height: 40,
						borderRadius: 12,
						background: 'linear-gradient(135deg, #0f2847, #1e4d8a)',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						flexShrink: 0,
					}}>
						<BankOutlined style={{ color: '#fff', fontSize: 18 }} />
					</div>
					<div style={{ flex: 1, minWidth: 0 }}>
						<div style={{ fontWeight: 700, fontSize: 14, color: '#0f2847', lineHeight: 1.3 }}>
							{row.name}
						</div>
						{row.inisial && (
							<Tag style={{
								borderRadius: 20, fontSize: 11, margin: '5px 0 0',
								background: '#e6f0fb', borderColor: '#91b4e8', color: '#1e4d8a', fontWeight: 600,
							}}>
								{row.inisial}
							</Tag>
						)}
					</div>
					<Tooltip title="Edit rekening">
						<Button size="middle" shape="circle" type="primary" ghost onClick={() => this.openEdit(row)}>
							<FormOutlined />
						</Button>
					</Tooltip>
				</div>
				<div style={{ padding: '10px 16px 14px' }}>
					<div style={{ marginBottom: 8 }}>
						<Text type="secondary" style={{ fontSize: 11 }}>Nomor Rekening</Text>
						<div style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600, color: '#222' }}>
							{row.nomor_rekening || <Text type="secondary" style={{ fontFamily: 'initial', fontWeight: 400 }}>—</Text>}
						</div>
					</div>
					<div>
						<Text type="secondary" style={{ fontSize: 11 }}>Nama Penerima</Text>
						<div style={{ fontSize: 13, color: '#222' }}>
							{row.nama_penerima || <Text type="secondary">—</Text>}
						</div>
					</div>
				</div>
			</Card>
		);
	}

	render() {
		const { errMsg, errTitle, loadingData, loadingButton, dataSource, isMobile, searchText, modalVisible, editingRow } = this.state;

		return (
			<div>
				<Helmet>
					<title>{configClass.title + ' | ' + config.get_site_info().name}</title>
					<style>{`
						.data-kabupaten-wrap .ant-table-thead > tr > th {
							background: linear-gradient(135deg, #0f284711, #1e4d8a11) !important;
							font-weight: 700;
							font-size: 13px;
							color: #0f2847;
							border-bottom: 2px solid #d0dff5;
						}
						.data-kabupaten-wrap .ant-table-tbody > tr > td {
							padding: 12px 16px;
							vertical-align: middle;
						}
						.data-kabupaten-wrap .ant-table-tbody > tr:hover > td {
							background: #e8f0fb !important;
						}
						.data-kabupaten-wrap .ant-table-tbody > tr:nth-child(even) > td {
							background: #fafafa;
						}
						.data-kabupaten-wrap .ant-table-tbody > tr:nth-child(even):hover > td {
							background: #e8f0fb !important;
						}
						.form-kabupaten-modal .ant-modal-content {
							border-radius: 16px;
							overflow: hidden;
							padding: 0;
							box-shadow: 0 12px 40px rgba(15,40,71,0.25);
						}
						.form-kabupaten-modal .ant-modal-body {
							padding: 0;
						}
						.form-kabupaten-modal .ant-form-item-label > label {
							font-weight: 600;
							color: #374151;
							font-size: 13px;
						}
						.form-kabupaten-modal .ant-input {
							border-radius: 8px !important;
						}
						.form-kabupaten-modal .ant-input:focus {
							border-color: #1e4d8a !important;
							box-shadow: 0 0 0 2px rgba(30,77,138,0.1) !important;
						}
					`}</style>
				</Helmet>

				<Breadcrumb style={{ marginBottom: 16 }}>
					{configClass.breadCrumb.map((item, i) => (
						<Breadcrumb.Item key={i}>{item}</Breadcrumb.Item>
					))}
				</Breadcrumb>

				<ErrorAlertModal
					visible={errMsg !== ''}
					title={errTitle}
					error={errMsg}
					onClose={() => this.setState({ errTitle: '', errMsg: '' })}
				/>

				<Alert
					message="Hanya Superadmin"
					description="Halaman ini hanya untuk mengubah inisial, nomor rekening, dan nama penerima setoran per kabupaten/kota. Data lain (nama, provinsi) bersifat referensi dan tidak dapat diubah di sini."
					type="info"
					showIcon
					style={{ marginBottom: 16, borderRadius: 10 }}
				/>

				<Card
					className="data-kabupaten-wrap"
					style={{ borderRadius: 14, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', overflow: 'hidden' }}
					bodyStyle={{ padding: 0 }}
				>
					{/* Header */}
					<div style={{
						padding: '16px 20px',
						background: 'linear-gradient(135deg, #0f2847, #1e4d8a)',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						flexWrap: 'wrap',
						gap: 10,
					}}>
						<div>
							<div style={{ color: '#fff', fontWeight: 700, fontSize: 17 }}>
								<BankOutlined style={{ marginRight: 8 }} />
								{configClass.title}
							</div>
							<div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginTop: 2 }}>
								Total {dataSource.length} kabupaten/kota
								{loadingData && ' · Memuat...'}
							</div>
						</div>
						<Tooltip title="Refresh data">
							<Button
								onClick={() => this.setState({ searchText: '' }, () => this.getData(''))}
								icon={<RedoOutlined />}
								style={{
									borderRadius: 8,
									background: 'rgba(255,255,255,0.15)',
									border: 'none',
									color: '#fff',
								}}
							>
								{!isMobile && 'Refresh'}
							</Button>
						</Tooltip>
					</div>

					{/* Search bar */}
					<div style={{
						padding: '12px 20px',
						borderBottom: '1px solid #f0f0f0',
						background: '#fff',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: 12,
						flexWrap: 'wrap',
					}}>
						<Text type="secondary" style={{ fontSize: 12 }}>
							{loadingData ? 'Memuat data...' : `${dataSource.length} data ditemukan`}
						</Text>
						<Input
							placeholder="Cari nama kabupaten/kota..."
							allowClear
							size="middle"
							prefix={<SearchOutlined style={{ color: '#bbb' }} />}
							style={{ borderRadius: 8, maxWidth: 340 }}
							value={searchText}
							onChange={this.handleSearchChange}
							onClear={() => this.setState({ searchText: '' }, () => this.getData(''))}
						/>
					</div>

					{isMobile ? (
						<div style={{ padding: 12 }}>
							{loadingData ? (
								<div style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>
									Memuat data...
								</div>
							) : dataSource.length === 0 ? (
								<div style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>
									Tidak ada data
								</div>
							) : (
								dataSource.map(row => this.renderMobileCard(row))
							)}
						</div>
					) : (
						<div style={{ padding: 0 }}>
							<Table
								columns={this._buildColumns()}
								dataSource={dataSource}
								loading={loadingData}
								rowKey="id"
								scroll={{ x: 'max-content' }}
								size="middle"
								pagination={false}
							/>
						</div>
					)}
				</Card>

				<Modal
					visible={modalVisible}
					onCancel={this.closeEdit}
					footer={null}
					closable={false}
					destroyOnClose
					width={isMobile ? '92%' : 460}
					className="form-kabupaten-modal"
				>
					{editingRow && (
						<Form
							layout="vertical"
							name="fkabupaten"
							onFinish={this.handleSubmit}
							initialValues={{
								inisial: editingRow.inisial,
								nomor_rekening: editingRow.nomor_rekening,
								nama_penerima: editingRow.nama_penerima,
							}}
						>
							{/* Header gradient, konsisten dengan gaya form lain di aplikasi ini */}
							<div style={{
								background: 'linear-gradient(135deg, #0f2847, #1e4d8a)',
								padding: '22px 24px',
								display: 'flex',
								alignItems: 'center',
								gap: 16,
							}}>
								<div style={{
									width: 52,
									height: 52,
									borderRadius: 14,
									background: 'rgba(255,255,255,0.15)',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									flexShrink: 0,
								}}>
									<BankOutlined style={{ color: '#fff', fontSize: 24 }} />
								</div>
								<div style={{ flex: 1, minWidth: 0 }}>
									<div style={{ color: '#fff', fontWeight: 700, fontSize: 17, lineHeight: 1.3 }}>
										Edit Rekening
									</div>
									<div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
										{editingRow.name}
									</div>
								</div>
								<Button
									shape="circle"
									icon={<CloseOutlined />}
									onClick={this.closeEdit}
									style={{
										background: 'rgba(255,255,255,0.15)',
										border: 'none',
										color: '#fff',
										flexShrink: 0,
									}}
								/>
							</div>

							{/* Body */}
							<div style={{ padding: '24px 24px 8px' }}>
								<Form.Item
									label={<span><IdcardOutlined style={{ color: '#1e4d8a', marginRight: 6 }} />Inisial</span>}
									name="inisial"
								>
									<Input placeholder="Inisial kabupaten/kota..." style={FIELD_STYLE} maxLength={250} />
								</Form.Item>
								<Form.Item
									label={<span><BankOutlined style={{ color: '#1e4d8a', marginRight: 6 }} />Nomor Rekening</span>}
									name="nomor_rekening"
								>
									<Input placeholder="Nomor rekening tujuan setoran..." style={FIELD_STYLE} maxLength={250} />
								</Form.Item>
								<Form.Item
									label={<span><UserOutlined style={{ color: '#1e4d8a', marginRight: 6 }} />Nama Penerima</span>}
									name="nama_penerima"
									style={{ marginBottom: 0 }}
								>
									<Input placeholder="Nama penerima rekening..." style={FIELD_STYLE} maxLength={250} />
								</Form.Item>
							</div>

							{/* Footer */}
							<div style={{
								padding: '18px 24px',
								marginTop: 12,
								display: 'flex',
								justifyContent: isMobile ? 'stretch' : 'flex-end',
								flexDirection: isMobile ? 'column' : 'row',
								gap: 10,
								borderTop: '1px solid #f0f0f0',
								background: '#fafafa',
							}}>
								<Button
									icon={<CloseCircleOutlined />}
									onClick={this.closeEdit}
									disabled={loadingButton}
									style={{ borderRadius: 10, ...(isMobile ? { width: '100%' } : {}) }}
								>
									Batal
								</Button>
								<Button
									type="primary"
									htmlType="submit"
									icon={<SaveOutlined />}
									loading={loadingButton}
									style={{
										borderRadius: 10,
										background: 'linear-gradient(135deg, #0f2847, #1e4d8a)',
										border: 'none',
										fontWeight: 600,
										...(isMobile ? { width: '100%' } : {}),
									}}
								>
									Simpan
								</Button>
							</div>
						</Form>
					)}
				</Modal>
			</div>
		);
	}
}

const mapStateToProps = function (state) {
	return state.configReducers;
}

export default connect(
	mapStateToProps,
	{
		initHandler,
		setConfig,
		ajaxViewHandler,
		ajaxHandler,
	}
)(DataKabupaten);
